// A file whose name is already taken where it is being uploaded (TF-105): what the files service
// says about the file already there, the name of a copy, and the choice a person made. Pure, so it
// is tested alone; the dialog is NameConflictDialog, the flow is in uploads.tsx.

export type ConflictChoice = 'existing' | 'copy' | 'version';

/** The file already in the folder, as the files service describes it with its 409 NAME_TAKEN. */
export interface ExistingFile {
  id: string;
  name: string;
  folderId: string | null;
  /** The folder's names from the top: [] for the drive's top level. */
  folderPath: string[];
  sizeBytes: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * The file that took the name, when an announce was refused for it; null for any other refusal. Read
 * from the error's shape (an ApiError: status, code, details), so this module needs nothing else.
 */
export function takenBy(error: unknown): ExistingFile | null {
  const refusal = error as { status?: unknown; code?: unknown; details?: { existing?: ExistingFile } } | null;
  if (!refusal || refusal.status !== 409 || refusal.code !== 'NAME_TAKEN') return null;
  const existing = refusal.details?.existing;
  return existing && typeof existing.id === 'string' ? existing : null;
}

const MAX_NAME = 255;
const pad = (value: number) => String(value).padStart(2, '0');

/**
 * The name of a copy: the date and time it is added, before the extension - "Offer (2026-09-30
 * 14.05).pdf". A second try (another copy in the same minute) adds the seconds, a third a number.
 * The base is shortened when the whole would pass the 255 characters a name may have.
 */
export function copyName(name: string, at: Date, attempt = 0): string {
  let stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}.${pad(at.getMinutes())}`;
  if (attempt >= 1) stamp += `.${pad(at.getSeconds())}`;
  if (attempt >= 2) stamp += ` ${attempt}`;
  const dot = name.lastIndexOf('.');
  const [base, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
  const suffix = ` (${stamp})${ext}`;
  return `${base.slice(0, Math.max(1, MAX_NAME - suffix.length))}${suffix}`;
}

const REMEMBERED = 'teamfolio.uploadConflictChoice';

/** The last choice in this browser session: offered as the default button next time, never applied unasked. */
export function rememberedChoice(): ConflictChoice | null {
  try {
    const value = window.sessionStorage.getItem(REMEMBERED);
    return value === 'existing' || value === 'copy' || value === 'version' ? value : null;
  } catch {
    return null;
  }
}

export function rememberChoice(choice: ConflictChoice): void {
  try {
    window.sessionStorage.setItem(REMEMBERED, choice);
  } catch {
    /* storage unavailable: nothing remembered */
  }
}
